import { useCallback, useEffect, useMemo, useState } from "react";
import { Modal, Button, TextInput } from "flowbite-react";
import { HiSearch, HiX, HiChevronUp, HiChevronDown } from "react-icons/hi";
import { MdDragIndicator } from "react-icons/md";
import { useTranslation } from "react-i18next";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const STORAGE_KEY = "customerColumnIds";
const STORAGE_ORDER_KEY = "customerColumnOrder";

/**
 * All available columns for the customer list table.
 * `must` columns cannot be unchecked and are always first.
 * `defaultChecked` columns are checked on first visit.
 */
const getAllColumns = (t) => [
  {
    id: "account",
    label: t("pages.customers.tableHeaders.account"),
    must: true,
    defaultChecked: true,
  },
  {
    id: "segment",
    label: t("pages.customers.tableHeaders.segment"),
    defaultChecked: true,
  },
  {
    id: "mrr",
    label: t("pages.customers.tableHeaders.mrr"),
    defaultChecked: true,
  },
  {
    id: "arr",
    label: t("pages.customers.tableHeaders.arr"),
    defaultChecked: true,
  },
  {
    id: "deltaMrr",
    label: t("pages.customers.tableHeaders.newMrr"),
    defaultChecked: true,
  },
  {
    id: "deltaArr",
    label: t("pages.customers.tableHeaders.newArr"),
    defaultChecked: true,
  },
  {
    id: "daysTillRenewal",
    label: t("pages.customers.tableHeaders.daysTillRenewal"),
    defaultChecked: true,
  },
  {
    id: "location",
    label: t("pages.customers.tableHeaders.location"),
    defaultChecked: true,
  },
  {
    id: "renewalForecast",
    label: t("pages.customers.tableHeaders.renewalForecast"),
    defaultChecked: true,
  },
  {
    id: "healthScore",
    label: "Health Score",
    defaultChecked: false,
  },
  {
    id: "nps",
    label: "NPS",
    defaultChecked: false,
  },
  {
    id: "csat",
    label: "CSAT",
    defaultChecked: false,
  },
  {
    id: "seatUtilization",
    label: "Seat Utilization",
    defaultChecked: false,
  },
  {
    id: "numEmployees",
    label: "Employees",
    defaultChecked: false,
  },
  {
    id: "csOwner",
    label: "CS Owner",
    defaultChecked: false,
  },
  {
    id: "lifecycleStage",
    label: "Lifecycle Stage",
    defaultChecked: false,
  },
  {
    id: "churnDate",
    label: "Churn Date",
    defaultChecked: false,
  },
];

/**
 * A single sortable column item in the right panel.
 * Uses @dnd-kit/sortable for smooth animated reordering.
 */
const SortableColumnItem = ({
  column,
  onRemove,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.id, disabled: column.must });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={[
        "flex items-center justify-between rounded-md border px-3 py-2",
        isDragging
          ? "border-blue-400 bg-blue-50 shadow-lg"
          : "border-gray-200 bg-gray-50 hover:border-gray-300",
      ].join(" ")}
    >
      <div className="flex items-center gap-2">
        {!column.must && (
          <button
            className="cursor-grab touch-none text-gray-400 hover:text-gray-600 active:cursor-grabbing"
            aria-label={`Drag to reorder ${column.label}`}
            {...attributes}
            {...listeners}
          >
            <MdDragIndicator className="h-4 w-4" />
          </button>
        )}
        <span className="text-sm text-gray-700">{column.label}</span>
      </div>
      {!column.must && (
        <div className="flex items-center gap-1">
          <button
            onClick={onMoveUp}
            disabled={isFirst}
            className="rounded p-0.5 text-gray-400 hover:bg-gray-200 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-30"
            aria-label={`Move ${column.label} up`}
          >
            <HiChevronUp className="h-4 w-4" />
          </button>
          <button
            onClick={onMoveDown}
            disabled={isLast}
            className="rounded p-0.5 text-gray-400 hover:bg-gray-200 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-30"
            aria-label={`Move ${column.label} down`}
          >
            <HiChevronDown className="h-4 w-4" />
          </button>
          <button
            onClick={onRemove}
            className="rounded p-0.5 text-gray-400 hover:bg-gray-200 hover:text-gray-600"
            aria-label={`Remove ${column.label}`}
          >
            <HiX className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export const CustomersListColumnCustomisation = ({
  isOpen,
  handleClose,
  columns,
  setColumns,
}) => {
  const { t } = useTranslation();
  const allColumns = useMemo(() => getAllColumns(t), [t]);

  // Local draft state so changes only apply on "Apply"
  const [draftSelectedIds, setDraftSelectedIds] = useState([]);
  const [draftOrder, setDraftOrder] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  // dnd-kit sensors: pointer (mouse/touch) + keyboard (a11y)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Initialize columns from localStorage on first mount
  useEffect(() => {
    const savedIds = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    const savedOrder = JSON.parse(
      localStorage.getItem(STORAGE_ORDER_KEY) || "[]",
    );

    let checkedIds;
    if (savedIds.length > 0) {
      checkedIds = savedIds;
    } else {
      checkedIds = allColumns
        .filter((col) => col.must || col.defaultChecked)
        .map((col) => col.id);
    }

    const orderedColumns = buildOrderedColumns(
      allColumns,
      checkedIds,
      savedOrder,
    );
    setColumns(orderedColumns);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync draft state when modal opens
  useEffect(() => {
    if (isOpen) {
      const currentChecked = columns
        .filter((col) => col.checked)
        .map((col) => col.id);
      setDraftSelectedIds(currentChecked);
      setDraftOrder(currentChecked);
      setSearchTerm("");
    }
  }, [isOpen, columns]);

  const handleToggleColumn = useCallback((columnId, isChecked) => {
    setDraftSelectedIds((prev) =>
      isChecked ? [...prev, columnId] : prev.filter((id) => id !== columnId),
    );
    setDraftOrder((prev) =>
      isChecked ? [...prev, columnId] : prev.filter((id) => id !== columnId),
    );
  }, []);

  const handleRemoveColumn = useCallback((columnId) => {
    setDraftSelectedIds((prev) => prev.filter((id) => id !== columnId));
    setDraftOrder((prev) => prev.filter((id) => id !== columnId));
  }, []);

  const handleRemoveAll = useCallback(() => {
    const mustIds = allColumns.filter((c) => c.must).map((c) => c.id);
    setDraftSelectedIds(mustIds);
    setDraftOrder(mustIds);
  }, [allColumns]);

  // Move up/down handlers for arrow buttons
  const handleMoveUp = useCallback((index) => {
    setDraftOrder((prev) => {
      if (index <= 0) return prev;
      return arrayMove(prev, index, index - 1);
    });
  }, []);

  const handleMoveDown = useCallback((index) => {
    setDraftOrder((prev) => {
      if (index >= prev.length - 1) return prev;
      return arrayMove(prev, index, index + 1);
    });
  }, []);

  // dnd-kit drag end handler
  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setDraftOrder((prev) => {
      const oldIndex = prev.indexOf(active.id);
      const newIndex = prev.indexOf(over.id);
      return arrayMove(prev, oldIndex, newIndex);
    });
  }, []);

  const handleApply = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(draftSelectedIds));
    localStorage.setItem(STORAGE_ORDER_KEY, JSON.stringify(draftOrder));

    const orderedColumns = buildOrderedColumns(
      allColumns,
      draftSelectedIds,
      draftOrder,
    );
    setColumns(orderedColumns);
    handleClose();
  }, [allColumns, draftSelectedIds, draftOrder, setColumns, handleClose]);

  const handleCancel = useCallback(() => {
    handleClose();
  }, [handleClose]);

  // Columns available in the left panel (filterable by search)
  const availableColumns = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return allColumns
      .filter((col) => !col.must)
      .filter((col) => !term || col.label.toLowerCase().includes(term));
  }, [allColumns, searchTerm]);

  // Ordered selected columns for the right panel
  const selectedColumns = useMemo(() => {
    const colMap = Object.fromEntries(allColumns.map((c) => [c.id, c]));
    return draftOrder
      .filter((id) => draftSelectedIds.includes(id))
      .map((id) => colMap[id])
      .filter(Boolean);
  }, [allColumns, draftOrder, draftSelectedIds]);

  const firstNonMustIdx = selectedColumns.findIndex((c) => !c.must);

  return (
    <Modal show={isOpen} size="4xl" onClose={handleCancel}>
      <Modal.Header>Choose which columns you see</Modal.Header>
      <Modal.Body>
        <div className="flex gap-6" style={{ minHeight: "400px" }}>
          {/* Left panel: available columns */}
          <div className="flex w-1/2 flex-col">
            <TextInput
              icon={HiSearch}
              placeholder="Search columns..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="mb-4"
            />
            <div
              className="flex-1 overflow-y-auto pr-2"
              style={{ maxHeight: "350px" }}
            >
              {availableColumns.map((column) => (
                <label
                  key={column.id}
                  className="mb-2 flex cursor-pointer items-center gap-2"
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    checked={draftSelectedIds.includes(column.id)}
                    onChange={(e) =>
                      handleToggleColumn(column.id, e.target.checked)
                    }
                  />
                  <span className="text-sm text-gray-700">{column.label}</span>
                </label>
              ))}
              {availableColumns.length === 0 && (
                <p className="text-sm text-gray-400">
                  No columns match your search.
                </p>
              )}
            </div>
          </div>

          {/* Right panel: sortable selected columns */}
          <div className="flex w-1/2 flex-col">
            <div className="mb-4 flex items-center justify-between">
              <span className="text-sm font-semibold uppercase text-gray-500">
                Selected Columns ({selectedColumns.length})
              </span>
            </div>
            <div
              className="flex flex-1 flex-col gap-1 overflow-y-auto"
              style={{ maxHeight: "350px" }}
            >
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={selectedColumns.map((c) => c.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {selectedColumns.map((column, index) => (
                    <SortableColumnItem
                      key={column.id}
                      column={column}
                      isFirst={index <= firstNonMustIdx}
                      isLast={index === selectedColumns.length - 1}
                      onRemove={() => handleRemoveColumn(column.id)}
                      onMoveUp={() => handleMoveUp(index)}
                      onMoveDown={() => handleMoveDown(index)}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="flex w-full items-center justify-between">
          <div className="flex gap-2">
            <Button color="blue" onClick={handleApply}>
              Apply
            </Button>
            <Button color="light" onClick={handleCancel}>
              Cancel
            </Button>
          </div>
          <button
            onClick={handleRemoveAll}
            className="text-sm text-red-500 hover:text-red-700"
          >
            Remove All Columns
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

/**
 * Build the ordered columns array used by the parent component.
 * Ensures `must` columns come first, then respects saved order.
 */
function buildOrderedColumns(allColumns, checkedIds, savedOrder) {
  const colMap = Object.fromEntries(allColumns.map((c) => [c.id, c]));

  // Must columns always first
  const mustCols = allColumns
    .filter((c) => c.must)
    .map((c) => ({ ...c, checked: true }));

  // Order non-must columns: use savedOrder if available, fallback to allColumns order
  const nonMustOrdered = [];
  const addedIds = new Set(mustCols.map((c) => c.id));

  // First add columns in saved order
  for (const id of savedOrder) {
    if (!addedIds.has(id) && colMap[id]) {
      nonMustOrdered.push({
        ...colMap[id],
        checked: checkedIds.includes(id),
      });
      addedIds.add(id);
    }
  }

  // Then add any remaining columns not in savedOrder
  for (const col of allColumns) {
    if (!addedIds.has(col.id)) {
      nonMustOrdered.push({
        ...col,
        checked: checkedIds.includes(col.id),
      });
      addedIds.add(col.id);
    }
  }

  return [...mustCols, ...nonMustOrdered];
}
